import { Type } from '@angular/core';
import { toast } from 'ngx-sonner';
import { OperatorFunction, catchError, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { BlockUiService } from '../../../../../services';
import {
  RxRequestOptions,
  RequiredRxRequestOptions,
  RequiredErrorMessages,
  OcpToastMessage,
} from '../../../interfaces';
import { AuthorizationStrategy } from '../../../../../strategies/authorization/authorization.strategy';

function showToast(message: OcpToastMessage): void {
  const title = message.summary ?? message.detail ?? '';
  const description = message.summary ? message.detail : undefined;
  const duration = message.life;
  switch (message.severity) {
    case 'success':
      toast.success(title, { description, duration });
      break;
    case 'error':
      toast.error(title, { description, duration });
      break;
    case 'warn':
      toast.warning(title, { description, duration });
      break;
    default:
      toast.info(title, { description, duration });
      break;
  }
}

export function handleRequestError<T>(
  blockUiService: BlockUiService,
  authorizationService: AuthorizationStrategy,
  UnauthorizedDialog: Type<unknown> | null,
  translateService: TranslateService,
  options?: RxRequestOptions
): OperatorFunction<T, T> {
  const defaultErrorMessages: RequiredErrorMessages = {
    '401': {
      severity: 'error',
      summary: translateService.instant('auth.unauthorized'),
      detail: translateService.instant('auth.session-expired-please-log-in-again'),
    },
  };

  const rxRequestDefaultOptions: RequiredRxRequestOptions = {
    errorMessages: defaultErrorMessages,
  };
  const mergedOptions: RequiredRxRequestOptions = {
    ...rxRequestDefaultOptions,
    ...options,
    errorMessages: {
      ...rxRequestDefaultOptions.errorMessages,
      ...options?.errorMessages,
    },
  };

  return catchError(error => {
    console.error(error);
    switch (error.status) {
      case 400:
        if (mergedOptions.errorMessages['400']) {
          showToast(mergedOptions.errorMessages['400']);
        }
        break;
      case 401:
        showToast(mergedOptions.errorMessages['401']);
        authorizationService.isUnauthorized(UnauthorizedDialog);
        blockUiService.block();
        break;
      case 403:
        console.warn('Forbidden access - insufficient permissions.');
        break;
      default:
        console.error('An unexpected error occurred:', error);
    }
    return throwError(() => error);
  });
}
